import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'react-feather';
import styled from 'styled-components';
import { ThemeInterface } from '../../interfaces/themeInterface';
import { useMarketSound, SoundMode } from './useMarketSound';

const monoStack =
  "'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const Button = styled.button<{ $isActive: boolean }>`
  border: 1px solid
    ${({ $isActive, theme }: { $isActive: boolean; theme: ThemeInterface }) =>
      $isActive
        ? theme.name === 'short'
          ? 'rgba(255, 255, 255, 0.52)'
          : 'rgba(0, 0, 0, 0.34)'
        : theme.name === 'short'
        ? 'rgba(255, 255, 255, 0.32)'
        : 'rgba(0, 0, 0, 0.2)'};
  background: ${({ $isActive, theme }: { $isActive: boolean; theme: ThemeInterface }) =>
    $isActive
      ? theme.name === 'short'
        ? 'linear-gradient(180deg, rgba(255, 204, 213, 0.4) 0%, rgba(255, 204, 213, 0.2) 100%)'
        : 'linear-gradient(180deg, rgba(0, 0, 255, 0.32) 0%, rgba(0, 0, 255, 0.14) 100%)'
      : theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.05) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.04) 100%)'};
  cursor: pointer;
  outline: none;
  padding: 7px;
  border-radius: 11px;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  transition:
    transform 0.16s ease,
    opacity 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: inset 0 1px 0
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'};

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
  }

  &:focus-visible {
    box-shadow:
      0 0 0 2px ${({ theme }: { theme: ThemeInterface }) =>
        theme.name === 'short' ? 'rgba(255, 255, 255, 0.24)' : 'rgba(0, 0, 0, 0.18)'},
      inset 0 1px 0
        ${({ theme }: { theme: ThemeInterface }) =>
          theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'};
  }

  svg {
    fill: none;
    stroke: ${({ theme }: { theme: ThemeInterface }) => theme.text};
    opacity: 0.9;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: ${({ theme }: { theme: ThemeInterface }) =>
    theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.05) 100%), rgba(0, 0, 255, 0.96)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.03) 100%), rgba(255, 204, 213, 0.96)'};
  border: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 14px;
  padding: 16px;
  min-width: 230px;
  z-index: 2400;
  box-shadow:
    inset 0 1px 0
      ${({ theme }: { theme: ThemeInterface }) =>
        theme.name === 'short' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.1)'},
    0 18px 38px ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.12)'};
  backdrop-filter: blur(7px);
`;

const PopoverRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 0.62rem;
  font-family: ${monoStack};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.74;
  min-width: 56px;
`;

const Slider = styled.input`
  flex: 1;
  height: 4px;
  appearance: none;
  background: ${({ theme }: { theme: ThemeInterface }) =>
    theme.name === 'short' ? 'rgba(255, 255, 255, 0.26)' : 'rgba(0, 0, 0, 0.22)'};
  border-radius: 2px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
    border-radius: 50%;
    cursor: pointer;
    border: 1px solid ${({ theme }: { theme: ThemeInterface }) => theme.background};
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 6px 8px;
  background: ${({ theme }: { theme: ThemeInterface }) =>
    theme.name === 'short' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  border: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.16)'};
  border-radius: 8px;
  font-size: 0.64rem;
  font-family: ${monoStack};
  letter-spacing: 0.04em;
  cursor: pointer;
`;

const TickCount = styled.div`
  font-size: 0.6rem;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.64;
  text-align: center;
  font-family: ${monoStack};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.14)'};
`;

const Wrapper = styled.div`
  position: relative;
`;

const TOOLTIP_DISMISSED_KEY = 'semn:sound-tooltip-dismissed';

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 2px;
  font-family: ${monoStack};
  font-size: 0.6rem;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.62;
  white-space: nowrap;
  pointer-events: none;
  text-align: center;
  line-height: 1;

  &::before {
    content: '.^.';
    display: block;
    margin-bottom: 2px;
  }
`;

const MODES: { value: SoundMode; label: string }[] = [
  { value: 'melodic', label: 'Melodic' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'noise', label: 'Noise' },
  { value: 'ambient', label: 'Ambient' },
];

export const MarketSoundButton: React.FC = () => {
  const { isEnabled, volume, mode, tickCount, toggle, setVolume, setMode } = useMarketSound();
  const [showPopover, setShowPopover] = useState(false);
  const [showTooltip, setShowTooltip] = useState(
    () => !localStorage.getItem(TOOLTIP_DISMISSED_KEY),
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dismissTooltip = () => {
    if (showTooltip) {
      setShowTooltip(false);
      localStorage.setItem(TOOLTIP_DISMISSED_KEY, '1');
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    dismissTooltip();
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      // Modifier key: toggle popover
      setShowPopover(!showPopover);
    } else {
      // Regular click: toggle sound
      toggle();
    }
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(!showPopover);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Button
        $isActive={isEnabled}
        onClick={handleButtonClick}
        onContextMenu={handleRightClick}
        title={`Market Sound ${isEnabled ? 'ON' : 'OFF'} (right-click for options)`}
      >
        {isEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
      </Button>

      {showTooltip && !showPopover && <Tooltip>hear the market</Tooltip>}

      {showPopover && (
        <Popover onClick={(e) => e.stopPropagation()}>
          <PopoverRow>
            <Label>Volume</Label>
            <Slider
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
            />
          </PopoverRow>

          <PopoverRow>
            <Label>Mode</Label>
            <Select value={mode} onChange={(e) => setMode(e.target.value as SoundMode)}>
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </PopoverRow>

          {isEnabled && <TickCount>Ticks: {tickCount.toLocaleString()}</TickCount>}
        </Popover>
      )}
    </Wrapper>
  );
};
